import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Forklift, Shield } from "lucide-react";
import { Trans } from "react-i18next";
import { cn } from "@/lib/utils";
import { store } from "../../store";
import elofleetLogoUrl from "../../images/elofleet-logo.svg";
import eloshieldUrl from "../../images/eloshield.jpg";
import showcaseAmr from "../../images/showcase_amr.png";
import { observer } from "mobx-react";

const PedestrianSafetySlide = observer((): JSX.Element => {
    const t = store.i18n.t;
    return (
        <div className="relative h-[210px] w-[340px]">
            <div className="absolute top-0 left-0 z-1 flex w-[150px] flex-col gap-3 rounded-md bg-white p-2 shadow-md">
                <div className="flex items-center gap-1.5 text-xs font-light tracking-widest text-red-600 uppercase">
                    <Shield size={13} />
                    {t("showcase.safety.tag")}
                </div>
                <div>
                    <div className="text-3xl font-bold text-gray-900">15</div>
                    <div className="mt-1 text-sm text-gray-500">
                        {t("showcase.safety.incidentsThisWeek")}
                    </div>
                </div>
            </div>
            <img
                src={eloshieldUrl}
                alt="ELOshield warehouse scene"
                className="absolute top-8 left-20 h-[150px] w-[230px] rounded-md object-cover"
            />
            <div className="border-border absolute top-42 left-32 box-border w-[210px] rounded-md border bg-white p-2 shadow-sm">
                <div className="flex gap-1">
                    <div className="h-1.5 flex-8 rounded-full bg-red-700" />
                    <div className="h-1.5 flex-4 rounded-full bg-red-500" />
                    <div className="h-1.5 flex-3 rounded-full bg-red-300" />
                </div>
                <div className="mt-0.5 flex gap-1 text-xs text-stone-400">
                    <span className="flex-8">{t("showcase.safety.pedestrian")}</span>
                    <span className="flex-4">{t("showcase.safety.vehicle")}</span>
                    <span className="flex-3">{t("showcase.safety.impact")}</span>
                </div>
            </div>
        </div>
    );
});

const AGVFleetControlSlide = observer((): JSX.Element => {
    const t = store.i18n.t;
    return (
        <div className="relative h-[195px] w-[335px]">
            <img
                src={showcaseAmr}
                alt="ELOshield warehouse scene"
                className="border-border absolute top-0 left-0 h-[150px] w-[245px] rounded-md border object-cover"
            />
            <div className="absolute top-14 left-20 flex w-[250px] flex-col gap-1.5 rounded-md bg-white p-4 shadow-md">
                <div className="flex items-center gap-1.5 text-xs font-light tracking-widest text-yellow-700 uppercase">
                    <Bot size={13} />
                    {t("showcase.agv.tag")}
                </div>
                <div>
                    <div className="text-2xl font-bold text-gray-900">7</div>
                    <div className="mt-1 text-sm text-gray-500">
                        {t("showcase.agv.robotsActive")}
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="bg-primary h-1.5 rounded-full" />
                    <div className="h-1.5 rounded-full bg-gray-300" />
                </div>
            </div>
        </div>
    );
});

const ForkliftFleetManagementSlide = observer((): JSX.Element => {
    const t = store.i18n.t;
    return (
        <div className="relative h-[180px] w-[335px] bg-white">
            <div className="flex items-center gap-1.5 p-4 text-xs font-light tracking-widest text-green-700 uppercase">
                <Forklift size={13} />
                {t("showcase.forklift.tag")}
            </div>
            <table className="text-muted-foreground w-full text-xs">
                <thead>
                    <tr className="border-b border-gray-300">
                        <th className="p-2 text-left font-medium">
                            {t("showcase.forklift.column.id")}
                        </th>
                        <th className="p-2 text-left font-medium">
                            {t("showcase.forklift.column.model")}
                        </th>
                        <th className="p-2 text-left font-medium">
                            {t("showcase.forklift.column.loginTime")}
                        </th>
                        <th className="p-2 text-left font-medium">
                            {t("showcase.forklift.column.driveTime")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="shadow-md">
                        <td className="px-2 py-2.5">TRK-041</td>
                        <td className="px-2 py-2.5">Still RX20</td>
                        <td className="px-2 py-2.5">4h 36m</td>
                        <td className="px-2 py-2.5">3h 22m</td>
                    </tr>
                    <tr className="-translate-y-1 scale-97 shadow-md blur-[0.5px]">
                        <td className="px-2 py-2.5">TRK-017</td>
                        <td className="px-2 py-2.5">Toyota 8FBE</td>
                        <td className="px-2 py-2.5">4h 12m</td>
                        <td className="px-2 py-2.5">2h 05m</td>
                    </tr>
                    <tr className="-translate-y-2 scale-94 shadow-md blur-[1px]">
                        <td className="px-2 py-2.5">TRK-093</td>
                        <td className="px-2 py-2.5">Jungheinrich EFG</td>
                        <td className="px-2 py-2.5">6h 30m</td>
                        <td className="px-2 py-2.5">5h 11m</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

const SLIDES = [
    {
        id: "agv",
        tagColor: "text-yellow-600",
        titleKey: "showcase.slide.agv.title",
        descriptionKey: "showcase.slide.agv.description",
        content: AGVFleetControlSlide,
    },
    {
        id: "safety",
        tagColor: "text-red-600",
        titleKey: "showcase.slide.safety.title",
        descriptionKey: "showcase.slide.safety.description",
        content: PedestrianSafetySlide,
    },
    {
        id: "forklift",
        tagColor: "text-green-600",
        titleKey: "showcase.slide.forklift.title",
        descriptionKey: "showcase.slide.forklift.description",
        content: ForkliftFleetManagementSlide,
    },
];

export const ShowcasePanel = observer((): JSX.Element => {
    const t = store.i18n.t;
    const currentLanguage = store.i18n.currentLanguage;

    const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
    const currentColor = SLIDES[currentSlideIndex].tagColor;
    const intervalIdRef = useRef<number | null>(null);

    const autoRotate = useCallback((): void => {
        if (intervalIdRef.current != null) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }

        const intervalId = setInterval(() => {
            setCurrentSlideIndex((prev) => (prev + 1) % SLIDES.length);
        }, 4000);
        intervalIdRef.current = intervalId;
    }, []);

    useEffect(() => {
        autoRotate();

        return () => {
            if (intervalIdRef.current != null) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
    }, [autoRotate]);

    const setCurrentSlideIndexWithAutoRotate = useCallback(
        (index: number) => {
            setCurrentSlideIndex(index);
            autoRotate();
        },
        [autoRotate],
    );

    return (
        <div className="relative hidden max-w-5xl flex-1 flex-col justify-center gap-4 overflow-hidden p-14 lg:flex 2xl:gap-8">
            <div className="relative z-10">
                <a href="https://www.elofleet.com" target="_blank" rel="noreferrer">
                    <img
                        src={elofleetLogoUrl}
                        alt="ELOfleet logo"
                        className="h-11 w-auto object-contain"
                    />
                </a>
            </div>
            <div className="relative z-10 flex flex-col gap-10 2xl:gap-15">
                <h1 className="max-w-3xl text-4xl leading-tight font-extrabold text-gray-900 2xl:text-5xl">
                    <Trans
                        i18nKey="showcase.headline"
                        components={{
                            color: (
                                <span
                                    key="color"
                                    className={cn("transition-colors duration-500", currentColor)}
                                />
                            ),
                        }}
                    />
                </h1>
                <div>
                    <div className="relative flex h-90 max-w-3xl items-center justify-center">
                        {SLIDES.map((slide, i) => {
                            const offset = (i - currentSlideIndex + SLIDES.length) % SLIDES.length;
                            const isActive = offset === 0;
                            const isRight = offset === 1;
                            const Content = slide.content;

                            return (
                                <div
                                    key={slide.id}
                                    onClick={() =>
                                        !isActive && setCurrentSlideIndexWithAutoRotate(i)
                                    }
                                    className={cn(
                                        "absolute min-h-[400px] w-[435px] rounded-2xl bg-white px-2 py-10 shadow-md transition-all duration-500 ease-in-out",
                                        currentLanguage === "en" && "w-[380px]",
                                        isActive
                                            ? "z-20 translate-x-0 scale-100"
                                            : "z-10 cursor-pointer blur-[2px]",
                                        !isActive &&
                                            isRight &&
                                            "translate-x-[30%] scale-[0.82] xl:translate-x-[60%]",
                                        !isActive &&
                                            !isRight &&
                                            "-translate-x-[30%] scale-[0.82] xl:-translate-x-[60%]",
                                    )}
                                >
                                    <div className="mt-2 px-2 text-center text-2xl font-bold text-gray-900">
                                        {t(slide.titleKey)}
                                    </div>
                                    <div className="mt-1 text-center leading-relaxed text-gray-500">
                                        {t(slide.descriptionKey)}
                                    </div>
                                    <div className="mt-3 flex items-center justify-center">
                                        <Content />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-14 flex w-full max-w-3xl items-center justify-center gap-2">
                        {SLIDES.map((slide, i) => (
                            <button
                                key={slide.id}
                                aria-label={t(slide.titleKey)}
                                onClick={() => setCurrentSlideIndexWithAutoRotate(i)}
                                className={cn(
                                    "h-2 cursor-pointer rounded-full transition-all duration-300",
                                    i === currentSlideIndex ? "w-6 bg-gray-800" : "w-2 bg-gray-300",
                                )}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});
